var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable max-lines */
import React, { forwardRef, useEffect, useImperativeHandle, useState, } from 'react';
import { useCFOptions } from '@connectrn/ds-ui-base';
import { CFContainer } from '../../components';
import { commonAttrs } from '../../utils';
import { FieldDecorator, FieldValidationBadge, SelectFieldFlyout, SelectFieldLabel, } from './components';
import { CFFieldElements } from './Field.styles';
import { CFSelectFieldElements } from './SelectField.styles';
export const CFSelectFieldCore = (_a, ref) => {
    var { allowMultiple = false, containerProps, disabled = false, fieldSize = 'md', iconLeft, iconRight = 'chevron-small-down', id, name, nativeFieldProps, onChange, onToggleOptions, options, optionsRenderer = 'flyout', optionListLabel, optionListSize, placeholder = 'Select', showValidationBadge = true, validationState = 'default', value = '' } = _a, rest = __rest(_a, ["allowMultiple", "containerProps", "disabled", "fieldSize", "iconLeft", "iconRight", "id", "name", "nativeFieldProps", "onChange", "onToggleOptions", "options", "optionsRenderer", "optionListLabel", "optionListSize", "placeholder", "showValidationBadge", "validationState", "value"]);
    const [optionsVisible, setOptionsVisible] = useState(false);
    const { FieldRootSelect, FieldWrapper } = CFFieldElements;
    const { SelectFieldFlyoutDismiss, SelectFieldOptionsWrapper } = CFSelectFieldElements;
    const [focused, setFocused] = useState(false);
    const { options: internalOptions, updateFromSelection, value: internalValue, } = useCFOptions({
        allowMultiple,
        initialOptions: options,
        initialValue: value,
        onChange,
    });
    const deselect = (value, hideChildren = true) => {
        if (hideChildren)
            setOptionsVisible(false);
        onFieldChange({ selected: false, value });
    };
    const onClickTrigger = () => {
        setOptionsVisible((val) => !val);
    };
    const onFieldBlur = (event) => {
        setFocused(false);
        if (nativeFieldProps === null || nativeFieldProps === void 0 ? void 0 : nativeFieldProps.onBlur)
            nativeFieldProps.onBlur(event);
    };
    const onFieldChange = (newOption) => {
        updateFromSelection(newOption);
    };
    const onFieldFocus = (event) => {
        setFocused(true);
        if (nativeFieldProps === null || nativeFieldProps === void 0 ? void 0 : nativeFieldProps.onBlur)
            nativeFieldProps.onBlur(event);
    };
    const renderOptionsFlyout = () => {
        const onConfirmSelection = () => toggleOptions();
        const onSelectOption = ({ value, selected }) => selected
            ? select(value, allowMultiple ? false : true)
            : deselect(value, allowMultiple ? false : true);
        return (React.createElement(SelectFieldOptionsWrapper, null,
            React.createElement(SelectFieldFlyoutDismiss, { onClick: () => toggleOptions() }),
            React.createElement(SelectFieldFlyout, { allowMultiple: allowMultiple, label: optionListLabel, onConfirmSelection: onConfirmSelection, onSelectOption: onSelectOption, options: internalOptions, placeholder: placeholder, size: optionListSize })));
    };
    const renderOptions = () => {
        if (!optionsVisible)
            return null;
        switch (optionsRenderer) {
            case 'flyout':
                return (React.createElement(SelectFieldOptionsWrapper, null, renderOptionsFlyout()));
            default:
                return (React.createElement(SelectFieldOptionsWrapper, null, optionsRenderer(internalOptions)));
        }
    };
    const select = (value, hideChildren = true) => {
        if (hideChildren)
            setOptionsVisible(false);
        onFieldChange({ selected: true, value });
    };
    const showOptions = () => setOptionsVisible(true);
    const toggleOptions = () => setOptionsVisible((val) => !val);
    useEffect(() => {
        if (onToggleOptions)
            onToggleOptions(optionsVisible);
    }, [optionsVisible]);
    useImperativeHandle(ref, () => {
        return {
            active: optionsVisible,
            deselect,
            select,
            showOptions,
            toggleOptions,
        };
    });
    return (React.createElement(CFContainer, Object.assign({ gap: 'none', style: {
            position: 'relative',
            zIndex: optionsVisible ? 2 : 1,
        } }, containerProps),
        React.createElement(FieldRootSelect, Object.assign({ allowMultiple: allowMultiple, disabled: disabled, fieldSize: fieldSize, focused: focused, hasSelection: internalValue.length > 0, onBlur: onFieldBlur, onClick: onClickTrigger, onFocus: onFieldFocus, optionsVisible: optionsVisible, validationState: validationState }, nativeFieldProps, commonAttrs(rest)),
            React.createElement(FieldWrapper, { fieldSize: fieldSize },
                React.createElement(FieldDecorator, { disabled: disabled, fieldSize: fieldSize, icon: iconLeft }),
                React.createElement(SelectFieldLabel, { fieldSize: fieldSize, options: internalOptions, placeholder: placeholder, value: internalValue }),
                React.createElement(FieldValidationBadge, { disabled: disabled, fieldSize: fieldSize, showValidationBadge: showValidationBadge, validationState: validationState }),
                React.createElement(FieldDecorator, { disabled: disabled, fieldSize: fieldSize, icon: iconRight }))),
        renderOptions(),
        React.createElement("input", { type: 'hidden', id: id, name: name, value: JSON.stringify(internalValue) })));
};
export const CFSelectField = forwardRef(CFSelectFieldCore);
